import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { ConfirmationOverlay } from "../../../../components/common/ConfirmationOverlay";
import { BackendErrorMessage } from "../../../../components/common/ErrorMessage";
import { Nav } from "../../../../components/common/Nav";
import { useUrl } from "../../../../hooks/common/useUrl";
import { transformCountryCodeForDisplay } from "../../../../helper/util";

import { useGetTeamOfPlayer, useGetTeams } from "@/hooks/teams";

import Head from "next/head";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { PlayerImage, TeamImage } from "../../../../components/common/Images";
import { NoItems } from "../../../../components/common/NoItems";
import { QueryWrapper } from "../../../../components/common/QueryWrapper";
import { useDeletePlayer, useGetPlayers } from "../../../../hooks/players";
import { useGetSeasons } from "../../../../hooks/season";
import { SeasonPlayer } from "../../../../models/Player";
import { SeasonTeam } from "../../../../models/Team";

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}
export default function PlayersPage() {
  const { seasonId } = useUrl();

  const { data, ...teamsResult } = useGetTeams({ seasonId });
  const { ...playersResult } = useGetPlayers({
    seasonId,
  });
  const { ...seasonsResult } = useGetSeasons();

  return (
    <>
      <Head>
        <title>TTBL Adminsystem | Spieler</title>
      </Head>
      <QueryWrapper
        loaderType="big"
        useQueryResult={[playersResult, teamsResult, seasonsResult]}
      >
        <div>
          <Nav />
          <div className="container">
            <div className=" rounded-b-lg  py-8 mb-10">
              <PlayersView seasonId={seasonId} />
            </div>
          </div>
        </div>
      </QueryWrapper>
    </>
  );
}

interface PlayersViewProps {
  seasonId: string;
}

export function AddPlayersView({ seasonId }: PlayersViewProps) {
  const { t } = useTranslation();
  const { customUrl } = useUrl();

  const { data: teams, ...teamsResult } = useGetTeams({ seasonId });

  return (
    <QueryWrapper useQueryResult={[teamsResult]}>
      {teams && teams.length > 0 && (
        <Link href={customUrl("/players/add")} className="btn uppercase mb-8">
          <FiPlus className="mr-2" /> {t("Add_Player")}
        </Link>
      )}
    </QueryWrapper>
  );
}

function PlayersView({ seasonId }: PlayersViewProps) {
  const { data: players, ...playersResult } = useGetPlayers({
    seasonId,
  });
  const { t } = useTranslation();

  return (
    <QueryWrapper useQueryResult={[playersResult]}>
      <>
        {players && players.length > 0 && (
          <table>
            <tbody>
              <tr className="table-header row_item">
                <th></th>
                <th className="w-1/12">{t("Label_Name")}</th>
                <th className="w-1/12">{t("Label_First_Name")}</th>
                <th className="w-1/12"></th>
                <th className="w-1/12">{t("Label_Nationality")}</th>
                <th>{t("Label_Club")}</th>
                <th>{t("Label_Role")}</th>
                <th className="text-right">{t("Label_Options")}</th>
              </tr>
              {players.map((player) => (
                <PlayerCard key={player.id} player={player} />
              ))}
            </tbody>
          </table>
        )}
        {!(players && players.length > 0) && <NoItems />}{" "}
      </>
    </QueryWrapper>
  );
}
interface PlayerCardProps {
  player: SeasonPlayer;
}
function PlayerCard({ player }: PlayerCardProps) {
  const { data: team, ...teamResult } = useGetTeamOfPlayer({
    playerId: player.id,
  });
  return (
    <QueryWrapper useQueryResult={[teamResult]}>
      {team && <PlayerCardView player={player} team={team} />}
    </QueryWrapper>
  );
}

interface PlayerCardViewProps {
  player: SeasonPlayer;
  team: SeasonTeam;
}

function PlayerCardView({ player, team }: PlayerCardViewProps) {
  const { customUrl } = useUrl();
  const { t } = useTranslation();
  const {
    mutate: deletePlayer,
    error: deleteError,
    isLoading: isPlayerLoading,
  } = useDeletePlayer({
    playerId: player.id,
  });
  return (
    <tr className="row_item">
      <td className="w-4">
        <Link
          href={customUrl(`/players/edit/${player.id}`)}
          className="flex w-full"
        >
          <PlayerImage player={player} size="sm" />
        </Link>
      </td>
      <td>
        <Link
          href={customUrl(`/players/edit/${player.id}`)}
          className="flex w-full"
        >
          {player.lastName}
        </Link>
      </td>
      <td>
        <Link
          href={customUrl(`/players/edit/${player.id}`)}
          className="flex w-full"
        >
          {player.firstName}
        </Link>
      </td>
      <td></td>
      <td>
        <Link
          href={customUrl(`/players/edit/${player.id}`)}
          className="flex w-full"
        >
          {transformCountryCodeForDisplay(player.nationality)}
        </Link>
      </td>
      <td>
        <Link
          href={customUrl(`/players/edit/${player.id}`)}
          className="flex w-full"
        >
          <div className="flex gap-4">
            <TeamImage team={team} />
            <div>{team.shortname}</div>
          </div>
        </Link>
      </td>
      <td>
        <Link
          href={customUrl(`/players/edit/${player.id}`)}
          className="flex w-full"
        >
          {t(player.role)}
        </Link>
      </td>

      <td>
        <div className="flex justify-end gap-2">
          <div>
            <Link
              href={customUrl(`/players/edit/${player.id}`)}
              className="mr-2"
            >
              <FiEdit />
            </Link>
          </div>
          <div>
            <ConfirmationOverlay
              isSubmitting={isPlayerLoading}
              onClick={() => deletePlayer()}
              confirmationMessage={t("Confirmation_Delete") as string}
            >
              <FiTrash2 className="ml-auto" />
            </ConfirmationOverlay>
          </div>
          <BackendErrorMessage error={deleteError} />
        </div>
      </td>
    </tr>
  );
}
